import React, { useEffect, useState } from "react";
import CredentialsDigitInput from "@/components/digit-input/DigitInput";
import { useTranslation } from "react-i18next";
import { otpService } from "@/services/continente-credentials/common/OtpService";
import { otpService as scOtpService } from "@/services/site-credentials/common/OtpService";
import { mfaService } from "@/services/continente-credentials/common/MfaService";
import { ResendStrategy } from "@/config/VerifyOtpFlowConfig";
import SimpleErrorMessage from "../error-message/new-error-message";
import "./otp-input.css";
import { Button } from "../ui/button";
import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";
import { tryCatch } from "@/utils/try-catch";
import { Info } from "lucide-react";

type InputProps = {
  onChange: (otp: string) => void;
  length?: number;
  isError?: boolean;
  errorMsg?: string;
  jwtToken?: string;
  isLoyalty?: boolean;
  onResendClick: (loading: boolean) => void;
  isEmail?: boolean;
  isMfa?: boolean;
  mfaUsername?: string;
  resendStrategy?: ResendStrategy;
  required?: boolean;
  onInvalid?: (e: any) => void;
};

const SPAM_MESSAGE_DELAY_MS = 20000;

export default function OtpInput({
  length = 6,
  isLoyalty = false,
  isEmail = false,
  isMfa = false,
  jwtToken = "",
  ...props
}: InputProps) {
  const [otp, setOtp] = useState<string[]>(
    Array.from({ length: length }, (_) => "")
  );

  const { t } = useTranslation();
  const { handleResponse } = useApiResponseHandler();

  const [showSpamMessage, setShowSpamMessage] = useState(false);
  const [otpInputError, setOtpInputError] = useState<string>("");
  const [seconds, setSeconds] = useState(0);
  const [otpInputJwt, setOtpInputJwt] = useState<string>(jwtToken);

  useEffect(() => {
    const initialInput = document.getElementById(`digit${0}`);

    if (initialInput != null) {
      initialInput.focus();
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {setShowSpamMessage(true)}, SPAM_MESSAGE_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => setTimer(), 1000);
    return () => clearTimeout(timerId);
  });

  const setTimer = () => {
    if (seconds > 0) {
      setSeconds(seconds - 1);
      setOtpInputError(
        t("otp_validation.resend_time_error", { time: seconds })
      );
    } else {
      setOtpInputError("");
      setSeconds(0);
    }
  };

  const changeInput = (event: any, position: number) => {
    const value = event.target.value;
    if (!isNaN(+value)) {
      //true if its a number
      if (value.length > 1) {
        const code = value.split("").map((char: any, index: any) => {
          if (Number.isInteger(Number(char))) {
            return Number(char);
          }

          return "";
        });

        const newOtp = [...otp];
        code.forEach((value: any, index: any) => {
          if (index < length) {
            newOtp[index] = value;
          }
        });
        setOtp(newOtp);

        props.onChange(newOtp.join(""));

        const initialInput = document.getElementById(`digit${0}`);

        if (initialInput != null) {
          initialInput.focus();
        }
      } else {
        const nextInput = document.getElementById(`digit${position + 1}`);

        if (nextInput != null) {
          nextInput.focus();
        }

        const newOtp = [...otp];
        newOtp[position] = value;

        setOtp(newOtp);

        props.onChange(newOtp.join(""));
      }
    }
  };

  const handleBackspace = (position: number) => {
    const actualInput = document.getElementById(
      `digit${position}`
    ) as HTMLInputElement;

    if (actualInput != null && !actualInput.value) {
      const prevInput = document.getElementById(`digit${position - 1}`);

      if (prevInput != null) {
        prevInput.focus();
      }

      const newOtp = [...otp];
      newOtp[position - 1] = "";

      setOtp(newOtp);

      props.onChange(newOtp.join(""));
    } else {
      actualInput.focus();
      const newOtp = [...otp];
      newOtp[position] = "";

      setOtp(newOtp);

      props.onChange(newOtp.join(""));
    }
  };

  const resendOTP = async () => {
    if (seconds > 0) {
      return;
    }

    setOtp(Array.from({ length }, () => ""));

    props.onChange("");
    setOtpInputError("");

    props.onResendClick(true);

    const buildResendRequest = () => {
      const commonPayload = { time: "0" };

      if (props.resendStrategy === "passwordRecover") {
        return otpService.resendPasswordRecoverOTP(commonPayload);
      }

      if (props.resendStrategy === "scPasswordRecover") {
        return scOtpService.resendPasswordRecoverOTP(commonPayload);
      }

      if (isLoyalty) {
        return otpService.resendLoyaltyOTP();
      }

      if (isMfa) {
        return mfaService.ResendMfaChalllenge(props.mfaUsername ?? "");
      }

      if (isEmail) {
        return otpService.resendEmailOTP();
      }

      return otpService.resendOTP();
    };

    const { data: response, error } = await tryCatch(buildResendRequest());

    if (error || !response) {
      setOtpInputError(t("common.unexpected_error"));
      props.onResendClick(false);
      return;
    }

    handleResponse(
      response,
      {
        onSuccess: (response) => {
          setOtpInputJwt(response.value.jwtToken);

          setSeconds(parseInt(response.value.time));
          setOtpInputError(
            t("otp_validation.resend_time_error", { time: response.value.time })
          );

          setShowSpamMessage(true);

          document.getElementById(`digit${0}`)?.focus();
        },
      },
      error
    );

    props.onResendClick(false);
  };

  const ariaLabel = isEmail
    ? t("accessibility.otpInput.email")
    : t("accessibility.otpInput.phonenumber");

  const DIGIT_LABEL_KEYS = [
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
  ] as const;

  function getAriaLabelDescription(digit: number) {
    const key = DIGIT_LABEL_KEYS[digit - 1];
    if (!key) return "";

    return t(`accessibility.otpInput.digits.${key}`);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-center verification-code w-full text-center gap-1">
        {otp.map((d, i) => (
          <CredentialsDigitInput
            {...props}
            id={`digit${i}`}
            key={i}
            value={d}
            ariaLabel={
              (i == 0 ? ariaLabel : "") + getAriaLabelDescription(i + 1)
            }
            onChange={(e) => changeInput(e, i)}
            onBackspace={() => handleBackspace(i)}
            required={true}
            onInvalid={props.onInvalid}
          />
        ))}
      </div>
      <SimpleErrorMessage
        id={"otp_input__error-message"}
        error={props.errorMsg}
      />
      <div className="flex justify-center text-[var(--on-surface-variant)] text-sm items-center gap-2">
        <span aria-hidden>
          {isEmail
            ? t("otp_validation.email_not_received_message1")
            : t("otp_validation.code_not_received_message1")}
        </span>
        {otpInputError === "" ? (
          <Button
            variant={"link"}
            size={"link"}
            type="button"
            onClick={resendOTP}
            className="w-auto p-0 h-auto"
            aria-label={t("accessibility.otpInput.resendLink")}
          >
            {isEmail
              ? t("otp_validation.email_not_received_message2")
              : t("otp_validation.code_not_received_message2")}
          </Button>
        ) : (
          <span className="link text-[var(--on-surface-variant)]" aria-hidden>
            {otpInputError}
          </span>
        )}
      </div>
      {showSpamMessage && (
        <div className="p-4 bg-[#e1f0fb] text-black border-[1px] border-[#bbd3e8] rounded-lg flex">
          <Info className="inline mr-4" />
          <div>
            {isEmail
              ? t("otp_validation.email_spam_message")
              : t("otp_validation.sms_spam_message")}
          </div>
        </div>
      )}
      {/* {import.meta.env.VITE_NODE_ENV !== "production" && (
        <div style={{ backgroundColor: "#ee7171", color: "white" }}>
          Os Otps serão ativados para testes! Se o valor 111111 não funcionar
          deverão procurar no Contacto o código enviado.
        </div>
      )} */}
    </div>
  );
}
